import type { BaseSyntheticEvent } from 'react';
import { forwardRef } from 'react';
import styled from 'styled-components';
import {
  HorizontalLayout,
  SubmitMessage,
  useFormSelector,
  useWidgetSelector
} from '@elfsight/widget-form-builder/widget';
import { useIsMobile } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import type { SMLSize } from '../../../types';
import {
  getFieldLineHeight,
  getFormWidth,
  getSubmitMessageFontSize,
  getTheme
} from '../../utils/form-element-utils';

type FormLayoutProps = {
  formSettings: any;
  onSubmit?(e: BaseSyntheticEvent): Promise<void>;
};

export const FormLayout = forwardRef<HTMLDivElement, FormLayoutProps>(
  function FormLayout({ formSettings, onSubmit }, forwardingRef) {
    const { formSuccessMessage, messageColor, barSize, barSizeMobile } =
      useSettings();

    const { submitted } = useWidgetSelector();
    const { status } = useFormSelector();
    const isMobile = useIsMobile();

    const derivedBarSize = isMobile ? barSizeMobile : barSize;

    return (
      <Container
        ref={forwardingRef}
        _size={derivedBarSize}
        _isMobile={isMobile}
        _isSubmitted={submitted}
        _textHeight={getFieldLineHeight(derivedBarSize, isMobile)}
      >
        {submitted && status === 'success' ? (
          <SubmitMessage
            fields={formSettings.fields}
            submitMessageTitle=""
            submitMessageText={formSuccessMessage}
            submitMessageButton={false}
            submitMessageTextColor={messageColor}
            submitMessageTextFontStyle={{
              fontSize: getSubmitMessageFontSize(derivedBarSize, isMobile),
              fontWeight: 'normal',
              fontStyle: 'normal'
            }}
          />
        ) : (
          <HorizontalLayout
            formSettings={formSettings}
            theme={getTheme(messageColor)}
            handleSubmit={onSubmit}
            hideErrors
          />
        )}
      </Container>
    );
  }
);

const Container = styled.div<{
  _size: SMLSize;
  _isMobile: boolean;
  _isSubmitted?: boolean;
  _textHeight?: number;
}>`
  max-width: ${({ _size, _isMobile, _isSubmitted }) =>
    _isSubmitted ? 'none' : getFormWidth(_size, _isMobile)}px;

  & input {
    height: ${({ _textHeight }) => _textHeight}px;
  }
`;
